import { useMemo } from "react";
import { Link } from "react-router-dom";
import s from "./WorkOrders.module.css";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { useWorkOrders } from "../../api/queries";
import {
  describeFilters,
  isoDay,
  matches,
  sortOrders,
  workOrdersCsv,
  workOrderStats,
} from "../../domain/workOrderReport";
import { Button, EmptyState, ErrorState, Skeleton } from "../../design/components";
import { formatDateTime } from "../../lib/format";
import { t } from "../../i18n";
import { useWorkOrderFilters } from "./useWorkOrderFilters";
import { WorkOrderStats } from "./WorkOrderStats";
import { downloadText, WorkOrderTable } from "./WorkOrdersPage";

/**
 * A work order report for exactly the filters on the list.
 *
 * It reads the same URL parameters as the list, so what was on the screen is
 * what is reported. Printing hides everything but the report; "Save as PDF" in
 * the browser's print dialog makes the file.
 */
export function WorkOrderReportPage() {
  const { site, serverTime, principal, tenant } = useSession();
  const { filters: f, params } = useWorkOrderFilters();
  const orders = useWorkOrders(f.status ? { status: f.status } : {});
  const now = useMemo(() => new Date(serverTime), [serverTime]);
  const rows = useMemo(
    () =>
      sortOrders(
        (orders.data?.items ?? []).filter((w) => matches(w, f, now)),
        f.sort,
        now,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orders.data, params.toString(), now],
  );
  const stats = useMemo(() => workOrderStats(rows, now), [rows, now]);
  const back = `/work-orders${params.toString() ? `?${params}` : ""}`;

  return (
    <Page eyebrow={t("{code} · maintenance", { code: site.code })} title="Work order report">
      <div className={s.report} data-testid="wo-report">
        <div className={s.reportActions}>
          <Button variant="primary" onClick={() => window.print()} disabled={!orders.data}>
            {t("Print / save as PDF")}
          </Button>
          <Button
            onClick={() =>
              downloadText(`work-orders-${site.code}-${isoDay(now)}.csv`, workOrdersCsv(rows), "text/csv;charset=utf-8")
            }
            disabled={!rows.length}
          >
            {t("Export CSV")}
          </Button>
          <Link to={back}>
            <Button variant="ghost">{t("Back to work orders")}</Button>
          </Link>
        </div>

        <header className={s.reportHead}>
          <h2 style={{ margin: 0 }}>
            {site.name
              ? t("Work orders — {code} · {name}", { code: site.code, name: site.name })
              : t("Work orders — {code} ", { code: site.code })}
          </h2>
          <div className={s.reportMeta}>{describeFilters(f).join(" · ")}</div>
          <div className={s.reportMeta}>
            {t("Generated {when} by {name}", { when: formatDateTime(now.toISOString()), name: principal.name })}
            {tenant ? ` · ${tenant}` : ""} ·{" "}
            {t("{n} of {total} orders on the platform", { n: rows.length, total: orders.data?.items.length ?? "—" })}
          </div>
          {orders.data?.truncated && (
            <div className={s.truncated} role="status">
              {orders.data.note ??
                t("The platform returned only the first page of work orders; this report covers that page.")}
            </div>
          )}
        </header>

        {orders.isPending ? (
          <Skeleton rows={8} height={18} />
        ) : orders.isError ? (
          <ErrorState error={orders.error} retry={() => void orders.refetch()} />
        ) : (
          <>
            <WorkOrderStats stats={stats} interactive={false} />
            {rows.length === 0 ? (
              <EmptyState>No work orders match these filters.</EmptyState>
            ) : (
              <WorkOrderTable rows={rows} now={now} full />
            )}
          </>
        )}
      </div>
    </Page>
  );
}
